<template>
  <div id="peerReviewEntryContentGrid">
    <div v-for="(value, key) in evaluation" :key="key">
      <peer-review-entry-content-row
        v-if="typeof value === 'number'"
        :name-of-criteria="criteria[key]"
        :is-overall-value="key === 'overallValue'"
        :targeted-user-id="targetedUserId"
        :value="evaluation[key]"
        @valueChanged="evaluation[key] = $event"
      ></peer-review-entry-content-row>
    </div>
    <div id="textAreaGrid">
      <div>
        <h5 class="main-color font-bold">{{ $t(localeConsts.strengths) }}</h5>
        <b-form-textarea
          id="strengthsTextArea"
          v-model="evaluation.strengths"
          class="textAreaLayout"
          :placeholder="$i18n.t(localeConsts.strengthsHint)"
          rows="4"
          :state="textAreaValidation.isStrengthTextAreaValid"
          @input="addTextAreaValidCheck()"
        ></b-form-textarea>
        <b-form-invalid-feedback>{{ $t(localeConsts.invalidTextArea) }}</b-form-invalid-feedback>
      </div>
      <div id="suggestedImprovementsTextArea">
        <h5 class="main-color font-bold">{{ $t(localeConsts.improvements) }}</h5>
        <b-form-textarea
          v-model="evaluation.improvements"
          class="textAreaLayout"
          :placeholder="$i18n.t(localeConsts.improvementsHint)"
          rows="4"
          :state="textAreaValidation.isImprovementsTextAreaValid"
          @input="addTextAreaValidCheck()"
        ></b-form-textarea>
        <b-form-invalid-feedback>{{ $t(localeConsts.invalidTextArea) }}</b-form-invalid-feedback>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import axios from '../../../axios'
import PeerReviewEntryContentRow from './PeerReviewEntryContentRow.vue'
import { mapGetters, mapMutations } from 'vuex'
export default {
  name: 'PeerReviewEntryContent',
  components: { PeerReviewEntryContentRow },
  props: {
    targetedUserId: Number
  },
  data() {
    return {
      localeConsts: {
        overallValue: 'PEERREVIEW.OVERALLVALUE',
        engagement: 'PEERREVIEW.ENGAGEMENT',
        teamWork: 'PEERREVIEW.TEAMWORK',
        communication: 'PEERREVIEW.COMMUNICATION',
        technicalSkills: 'PEERREVIEW.TECHNICALSKILLS',
        expertise: 'PEERREVIEW.EXPERTISE',
        save: 'PEERREVIEW.SAVE',
        strengths: 'PEERREVIEW.STRENGTHS',
        strengthsHint: 'PEERREVIEW.STRENGTHHINT',
        improvements: 'PEERREVIEW.IMPROVEMENTS',
        improvementsHint: 'PEERREVIEW.IMPROVEMENTSHINT',
        invalidTextArea: 'PEERREVIEW.INVALIDTEXTAREAERROR'
      },
      evaluation: {
        overallValue: 50,
        engagement: 1,
        teamWork: 1,
        communication: 1,
        technicalSkills: 1,
        expertise: 1,
        is_self: false,
        strengths: '',
        improvements: ''
      },
      peerReviewRound: null,
      author_student: null
    }
  },
  computed: {
    ...mapGetters('peerReview', ['getOverallRangeValueByKey', 'getSaveFlag', 'getExistingReviews']),
    criteria() {
      return {
        overallValue: this.$i18n.t(this.localeConsts.overallValue),
        engagement: this.$i18n.t(this.localeConsts.engagement),
        teamWork: this.$i18n.t(this.localeConsts.teamWork),
        communication: this.$i18n.t(this.localeConsts.communication),
        technicalSkills: this.$i18n.t(this.localeConsts.technicalSkills),
        expertise: this.$i18n.t(this.localeConsts.expertise)
      }
    },
    hasActiveRound() {
      return this.peerReviewRound !== null
    },
    textAreaValidation() {
      return {
        isStrengthTextAreaValid: this.evaluation.strengths.length > 50,
        isImprovementsTextAreaValid: this.evaluation.improvements.length > 50
      }
    },
    reviewIsValid() {
      if (this.textAreaValidation.isStrengthTextAreaValid && this.textAreaValidation.isImprovementsTextAreaValid) {
        return this.textAreaValidation.isStrengthTextAreaValid && this.textAreaValidation.isImprovementsTextAreaValid
      }
      return false
    }
  },
  watch: {
    //eslint-disable-next-line
    getSaveFlag: function (newValue, oldValue) {
      if (newValue === true) {
        this.createPeerReview()
      }
    },
    getExistingReviews(reviews) {
      if (!reviews || reviews.length === 0 || this.peerReviewRound === null) return
      const draft = reviews.find(
        (review) => review.targeted_student === this.targetedUserId && review.peer_review_round === this.peerReviewRound
      )
      if (draft) {
        this.populateFromDraft(draft)
      }
    }
  },
  methods: {
    ...mapMutations('peerReview', ['setTextAreaValidEntry', 'setSaveFlag', 'setOverallRangeValue']),
    printObject() {
      this.createPeerReview()
    },
    addTextAreaValidCheck() {
      this.setTextAreaValidEntry({ key: this.targetedUserId, value: this.reviewIsValid })
    },
    populateFromDraft(draft) {
      this.evaluation.overallValue = draft.global_mark
      this.evaluation.engagement = draft.engagement_level
      this.evaluation.teamWork = draft.team_work_ability_level
      this.evaluation.communication = draft.communication_skills_level
      this.evaluation.technicalSkills = draft.technical_skills_level
      this.evaluation.expertise = draft.practical_skills_level
      this.evaluation.strengths = draft.skills_comments
      this.evaluation.improvements = draft.improvements_comments

      this.setOverallRangeValue({
        key: this.targetedUserId,
        overallRangeValue: draft.global_mark
      })

      this.addTextAreaValidCheck()
    },

    async createPeerReview() {
      try {
        const peerReviewObject = await this.mapInternalObjectToPostObject()
        await axios.post('peer-reviews/', peerReviewObject)

        this.$root.$emit('peer-review-saved', { success: true })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Save Peer Review Error:', error)
        this.$root.$emit('peer-review-saved', { success: false })
      }
    },

    async mapInternalObjectToPostObject() {
      const userId = this.$store.getters['authentication/getUserId']
      const projectId = this.$store.getters['authentication/getProjectId']

      await this.fetchAndIdentifyActivePeerReviewRound()

      if (this.targetedUserId === userId) {
        this.evaluation.is_self = true
      }
      return {
        global_mark: this.getOverallRangeValueByKey(this.targetedUserId),
        engagement_level: this.evaluation.engagement,
        practical_skills_level: this.evaluation.expertise,
        team_work_ability_level: this.evaluation.teamWork,
        communication_skills_level: this.evaluation.communication,
        technical_skills_level: this.evaluation.technicalSkills,
        skills_comments: this.evaluation.strengths,
        improvements_comments: this.evaluation.improvements,
        peer_review_round: this.peerReviewRound,
        author_student: userId,
        targeted_student: this.targetedUserId,
        project: projectId,
        is_self: this.evaluation.is_self
      }
    },
    async fetchAndIdentifyActivePeerReviewRound() {
      const response = await axios.get('peer-review-rounds/')
      const reviewRounds = response.data

      const now = moment()

      reviewRounds.forEach((reviewRound) => {
        const startDate = moment(reviewRound.start_date)
        const endDate = moment(reviewRound.end_date)

        if (now.isSameOrAfter(startDate) && now.isSameOrBefore(endDate)) {
          this.peerReviewRound = reviewRound.id
        }
      })
    },
    reviewRoundIsActive(date) {
      return moment().isSameOrBefore(moment(date))
    }
  },
  async created() {
    await this.fetchAndIdentifyActivePeerReviewRound()

    const existingReviews = this.$store.getters['peerReview/getExistingReviews']

    if (existingReviews && this.peerReviewRound !== null) {
      const draft = existingReviews.find(
        (review) => review.targeted_student === this.targetedUserId && review.peer_review_round === this.peerReviewRound
      )
      if (draft) {
        this.populateFromDraft(draft)
      }
    }
  }
}
</script>

<style>
#peerReviewEntryContentGrid {
  display: flex;
  flex-direction: column;
}

.peerReviewEntryRow {
  display: flex;
  flex-direction: row;
  align-items: baseline;
}

#textAreaGrid {
  display: grid;
  grid-template-columns: 4fr 5px 5fr;
}

.textAreaLayout {
  margin-right: 10px;
}

#suggestedImprovementsTextArea {
  grid-column: 3;
}

#strengthTextArea {
  grid-column: 1;
}
</style>
